export const LANDING_PAGES_GUIDE = {
  id: "landing-pages",
  title: "Campaign Landing Pages",
  description:
    "How to create landing pages with lead-capture forms that automatically create contacts in Insights.",
  icon: "FileText",
  version: "1.0.0",
  lastUpdated: "2026-03-08",
  sections: [
    /* ---------------------------------------------------------- */
    /*  Overview                                                   */
    /* ---------------------------------------------------------- */
    {
      id: "overview",
      title: "Overview",
      content: [
        {
          type: "paragraph",
          text: "Landing pages let you create public-facing web pages with built-in lead-capture forms. When a visitor submits the form, the platform automatically:",
        },
        {
          type: "steps",
          items: [
            "**Creates or updates a contact** in Insights with the submitted information",
            "**Tags the contact** with configurable source labels and tags (e.g. \"lead\", campaign name)",
            "**Stores the submission** so you can review it later in the admin panel",
            "**Sends notification emails** to your team (optional)",
          ],
        },
        {
          type: "paragraph",
          text: "Landing pages use the same editor as content pages (rich text with TipTap) but add a form builder panel for configuring the lead-capture form.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Creating a Landing Page                                    */
    /* ---------------------------------------------------------- */
    {
      id: "creating",
      title: "Creating a Landing Page",
      content: [
        {
          type: "steps",
          items: [
            "Go to the **Content Pages** section from the sidebar",
            "Click the **Landing Pages** tab at the top to switch to landing page view",
            "Click the **New Landing Page** button",
            "Enter a **title** — this becomes the page heading and is used to generate the URL slug",
            "Write your page content in the **rich text editor** — this appears above the form on the public page",
            "Configure the form fields and settings in the **Form Builder** panel below the editor",
            "Set the page status to **Published** when you're ready to make it live",
          ],
        },
        {
          type: "callout",
          variant: "info",
          text: "The URL slug is automatically generated from the title. For example, a page titled \"Q2 Webinar Registration\" gets the slug `q2-webinar-registration` and is accessible at `/api/page/q2-webinar-registration`.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Form Builder                                               */
    /* ---------------------------------------------------------- */
    {
      id: "form-builder",
      title: "Configuring Form Fields",
      content: [
        {
          type: "paragraph",
          text: "The Form Builder panel appears below the content editor for landing pages. New landing pages come with five default fields: First Name, Last Name, Email (all required), plus Company and Phone (optional).",
        },
        {
          type: "heading",
          text: "Field Types",
        },
        {
          type: "table",
          headers: ["Type", "Description"],
          rows: [
            ["**Text**", "Single-line text input — use for names, job titles, etc."],
            ["**Email**", "Email input with format validation"],
            ["**Phone**", "Phone number input"],
            ["**Text Area**", "Multi-line text input — use for comments or messages"],
            ["**Dropdown**", "Select from predefined options — you specify the options as a comma-separated list"],
            ["**Checkbox**", "A single checkbox — use for consent, opt-in, or yes/no questions"],
          ],
        },
        {
          type: "heading",
          text: "Managing Fields",
        },
        {
          type: "steps",
          items: [
            "**Add a field** — click the \"+ Add Field\" button, fill in the field name (key), label, type, and optional placeholder, then click \"Add Field\"",
            "**Reorder fields** — use the up/down arrows on each field row to change the display order",
            "**Toggle required** — click the \"req\"/\"opt\" button on each field to make it required or optional",
            "**Remove a field** — click the trash icon on any field row",
          ],
        },
        {
          type: "callout",
          variant: "tip",
          text: "The field **name** (key) is the internal identifier stored in the database. Use camelCase names like `jobTitle` or `companySize`. The **label** is what the visitor sees on the form.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Form Settings                                              */
    /* ---------------------------------------------------------- */
    {
      id: "form-settings",
      title: "Form Settings",
      content: [
        {
          type: "paragraph",
          text: "Below the field list, you can configure how the form behaves:",
        },
        {
          type: "table",
          headers: ["Setting", "Description"],
          rows: [
            ["**Submit Button Label**", "The text on the submit button (default: \"Submit\")"],
            ["**Source Label**", "How this contact source appears in Insights (default: \"landing-page\"). Use something descriptive like \"q2-campaign\" or \"webinar-registration\""],
            ["**Source Tags**", "Comma-separated tags applied to new contacts (default: \"lead\"). Example: \"lead, webinar, q2-2026\""],
            ["**Notification Emails**", "Comma-separated email addresses that receive a notification whenever someone submits the form"],
            ["**Thank-You Message**", "The message shown after successful submission. Supports plain text"],
            ["**Thank-You Redirect URL**", "If set, the visitor is redirected to this URL instead of seeing the thank-you message. Use for custom thank-you pages or next steps"],
          ],
        },
        {
          type: "callout",
          variant: "info",
          text: "If both a redirect URL and a thank-you message are configured, the redirect takes priority. The message is only shown when there is no redirect URL.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  How Submissions Work                                       */
    /* ---------------------------------------------------------- */
    {
      id: "how-submissions-work",
      title: "How Submissions Work",
      content: [
        {
          type: "paragraph",
          text: "When a visitor fills out and submits the form on your landing page, the following happens:",
        },
        {
          type: "steps",
          items: [
            "**Validation** — required fields are checked, and the email format is validated",
            "**Anti-spam** — a hidden honeypot field catches bots (submissions from bots are silently discarded)",
            "**Contact creation** — Insights creates a new contact or updates an existing one (matched by email). The contact is tagged with your configured source label and tags, and set to **pending** review status",
            "**Submission stored** — the full submission data is saved and linked to the landing page for your records",
            "**Notifications sent** — if you configured notification emails, each address receives an email with the submission details in a table format",
            "**Thank-you** — the visitor sees your thank-you message or is redirected to your configured URL",
          ],
        },
        {
          type: "callout",
          variant: "tip",
          text: "Custom form fields (anything beyond firstName, lastName, email, phone, company) are stored in the contact's **customFields** along with the landing page slug and title. You can see these in the contact detail view.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Viewing Submissions                                        */
    /* ---------------------------------------------------------- */
    {
      id: "viewing-submissions",
      title: "Viewing Submissions",
      content: [
        {
          type: "steps",
          items: [
            "Go to **Content Pages** and click the **Landing Pages** tab",
            "Click on a landing page to open the editor",
            "Scroll down past the form builder to the **Submissions** section",
            "Click **View Submissions** to load and display the submissions table",
          ],
        },
        {
          type: "paragraph",
          text: "The submissions table shows the date, email address, and name for each submission. Click the expand button on any row to see the full submitted data.",
        },
        {
          type: "callout",
          variant: "info",
          text: "Submissions are only visible for saved landing pages (not when creating a new one). The submissions section loads data on demand to keep the editor fast.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Publishing and Public URL                                  */
    /* ---------------------------------------------------------- */
    {
      id: "publishing",
      title: "Publishing & Public URL",
      content: [
        {
          type: "paragraph",
          text: "Landing pages follow the same publishing workflow as content pages:",
        },
        {
          type: "table",
          headers: ["Status", "Behaviour"],
          rows: [
            ["**Draft**", "The page is not accessible to the public. Only visible in the admin panel"],
            ["**Published**", "The page is live and accessible at its public URL. The form accepts submissions"],
          ],
        },
        {
          type: "paragraph",
          text: "The public URL for any page follows the pattern:",
        },
        {
          type: "callout",
          variant: "info",
          text: "`/api/page/{slug}` — for example, `/api/page/q2-webinar-registration`",
        },
        {
          type: "paragraph",
          text: "You can share this URL in campaigns, emails, or social media posts. The page renders with clean styling including your content and the lead-capture form.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Content vs Landing Pages                                   */
    /* ---------------------------------------------------------- */
    {
      id: "content-vs-landing",
      title: "Content Pages vs Landing Pages",
      content: [
        {
          type: "table",
          headers: ["Feature", "Content Page", "Landing Page"],
          rows: [
            ["Rich text editor", "Yes", "Yes"],
            ["Lead-capture form", "No", "Yes"],
            ["Form builder panel", "No", "Yes"],
            ["Creates contacts on submit", "No", "Yes"],
            ["Submission history", "No", "Yes"],
            ["Public URL", "Yes", "Yes"],
            ["Draft/Published workflow", "Yes", "Yes"],
          ],
        },
        {
          type: "paragraph",
          text: "Both page types share the same editor, the same URL structure, and the same publishing workflow. The only difference is that landing pages have a form attached.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Troubleshooting                                            */
    /* ---------------------------------------------------------- */
    {
      id: "troubleshooting",
      title: "Troubleshooting",
      content: [
        {
          type: "faq",
          items: [
            {
              question: "The form doesn't appear on the public page",
              answer:
                "Check that:\n\n- The page type is set to **Landing Page** (not Content Page)\n- The page has at least one form field configured\n- The page status is **Published**\n\nThe form only renders on published landing pages with configured fields.",
            },
            {
              question: "Submissions aren't creating contacts",
              answer:
                "The form requires a valid email address in a field named `email`. Make sure your form has an email-type field with the name `email` (this is included by default). Without a valid email, the submission will be rejected with a validation error.",
            },
            {
              question: "I'm not receiving notification emails",
              answer:
                "Check the **Notification Emails** setting in the form builder. Email addresses should be comma-separated. Notification emails are sent via SendGrid — if SendGrid is not configured for your tenant, notifications won't be sent (but submissions will still be stored).",
            },
            {
              question: "The thank-you redirect isn't working",
              answer:
                "Make sure the redirect URL includes the full address with `https://`. The redirect happens on the client side after a successful submission. If the URL is invalid or unreachable, the browser may show an error.",
            },
            {
              question: "Duplicate contacts from the same person",
              answer:
                "The system matches contacts by email address. If the same person submits the form twice with the same email, their existing contact is updated rather than creating a duplicate. Each submission is still recorded separately in the submissions history.",
            },
            {
              question: "I changed the form fields but the public page still shows the old form",
              answer:
                "Make sure you've saved the page after changing the form configuration. The public page is served from the saved data in the database, so unsaved changes won't appear. The page has a 5-minute cache, so changes may take up to 5 minutes to appear.",
            },
          ],
        },
      ],
    },
  ],
};
